import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import api from "../utils/api/axiosInstance"; // ✅ pre-configured Axios
import { mergeGuestIntentAfterAuth } from "../utils/guestIntent";
import {
  clearMatchIntentStorage,
  handleAuthUserChange,
  reconcileMatchIntentStorageForUser,
} from "../utils/homeChatMatchPersistence";
import { splitFullName } from "../utils/adminHelpers";

const UserAuthContext = createContext();
export const useUserAuth = () => useContext(UserAuthContext);

const buildFullName = (u) =>
  `${u?.firstName || ""} ${u?.lastName || ""}`.trim();

const normalizeUser = (u) => {
  if (!u || !u._id) return null;

  // If API already provides first/last, keep them.
  let firstName = u.firstName ?? "";
  let lastName = u.lastName ?? "";

  // Back-compat: derive from legacy `name` if missing
  if ((!firstName && !lastName) && u.name) {
    const s = splitFullName(u.name);
    firstName = s.firstName;
    lastName = s.lastName;
  }

  const fullName = buildFullName({ firstName, lastName });
  const displayName = firstName || u.username || u.email || fullName || "";

  return {
    ...u,
    firstName,
    lastName,
    fullName,
    displayName,
    needsShoppingProfileOnboarding: Boolean(u.needsShoppingProfileOnboarding),
  };
};

const UserAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userAuthChecked, setUserAuthChecked] = useState(false);
  const previousUserIdRef = useRef(undefined);

  const setNormalizedUser = useCallback((nextUser) => {
    setUser((previousUser) => {
      const resolvedUser =
        typeof nextUser === "function" ? nextUser(previousUser) : nextUser;
      return normalizeUser(resolvedUser);
    });
  }, []);

  const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const applyAuthUser = useCallback(async (normalized) => {
    const nextUserId = normalized?._id || normalized?.id || null;
    handleAuthUserChange(previousUserIdRef.current, nextUserId);
    previousUserIdRef.current = nextUserId;
    reconcileMatchIntentStorageForUser(nextUserId);
    setUser(normalized);
    if (normalized) {
      await mergeGuestIntentAfterAuth(api);
    }
  }, []);

  // ✅ Fetch user helper
  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get("/users/me", { withCredentials: true });
      const normalized = normalizeUser(res.data);
      await applyAuthUser(normalized);
    } catch (err) {
      const nextUserId = null;
      handleAuthUserChange(previousUserIdRef.current, nextUserId);
      previousUserIdRef.current = nextUserId;
      reconcileMatchIntentStorageForUser(nextUserId);
      setUser(null);
      if (err.response?.status !== 401) {
        console.error("❌ Fetch user error:", err.response?.data || err.message);
      }
    } finally {
      setUserAuthChecked(true);
    }
  }, [applyAuthUser]);

  // 🔹 Run once on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // ✅ Login (supports rememberMe)
  const login = async (identifier, password, rememberMe = false) => {
    const payload = isEmail(identifier)
      ? { email: identifier, password, rememberMe }
      : { username: identifier, password, rememberMe };

    try {
      await api.post("/authuser/login", payload, { withCredentials: true }); // cookies set by server
      await fetchUser();
      return { success: true };
    } catch (err) {
      // Extract error message for rate limiting and other errors
      const errorMessage = err.response?.data?.message || "Login failed";
      const retryAfter = err.response?.data?.retryAfter; // Minutes until retry is allowed
      
      if (err.response?.status === 429) {
        // Rate limit error
        return { 
          success: false, 
          error: errorMessage,
          retryAfter: retryAfter,
          isRateLimited: true
        };
      } else if (err.response?.status === 403) {
        // Account suspended/banned
        return {
          success: false,
          error: errorMessage,
          isSuspended: true
        };
      } else if (err.response?.status !== 401) {
        console.error("❌ Login error:", err.response?.data || err.message);
      }
      return { success: false, error: errorMessage };
    }
  };

  // ✅ Logout
  const logout = async () => {
    try {
      await api.post("/authuser/logout", null, { withCredentials: true });
    } catch (err) {
      console.error("❌ Logout failed:", err.response?.data || err.message);
    } finally {
      clearMatchIntentStorage();
      previousUserIdRef.current = null;
      setUser(null);
      setUserAuthChecked(true);
    }
  };

  return (
    <UserAuthContext.Provider
      value={{
        user,                // now includes { firstName, lastName, fullName, displayName }
        login,
        logout,
        setUser: setNormalizedUser,
        refreshUser: fetchUser,
        userAuthChecked,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export default UserAuthProvider;
